import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/Order_item_requests_SalvarInput';
import '../contracts/Order_item_requests_SalvarTodosInput.ts';
import '../contracts/Order_item_requests_UploadPhotoInput';
import '../contracts/Order_item_requests_VerificarInput.ts';
import '../contracts/Order_item_requests_ConfirmarSaidaInput.ts';

import '../contracts/Order_item_requests_ListaResponse';
import '../contracts/Order_item_requests_RemoverResponse';
import '../contracts/Order_item_requests_SalvarResponse';
import '../contracts/Order_item_requests_SalvarTodosResponse';
import '../contracts/Order_item_requests_UploadPhotoResponse';
import '../contracts/Order_item_requests_VerificarResponse';
import type { Order_item_requests_SalvarInput } from '../contracts/Order_item_requests_SalvarInput';
import type { Order_item_requests_SalvarTodosInput } from '../contracts/Order_item_requests_SalvarTodosInput';
import type { Order_item_requests_VerificarInput } from '../contracts/Order_item_requests_VerificarInput';
export function useSOOrderItemRequests() {
  const isLoading = ref(false);
  const error = ref(null);
  const basePath = '/index.php/so/order_item_requests';

  const fetchByOrderItem = async (orderItemId: number) => {
    log('useSOOrderItemRequests', 'fetchByOrderItem', 'Fetching order item requests', { orderItemId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/lista/${orderItemId}`, { responseContract: 'Order_item_requests_ListaResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar solicitações de item de pedido');
    } catch (err) {
      logError('useSOOrderItemRequests', 'fetchByOrderItem', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const saveAll = async (orderItemId: any, requests: any) => {
    const payload: Order_item_requests_SalvarTodosInput = { requests };
    log('useSOOrderItemRequests', 'saveAll', 'Saving all order item requests', {
      orderItemId,
      payload
    });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/salvar_todos/${orderItemId}`, payload, { inputContract: 'Order_item_requests_SalvarTodosInput', responseContract: 'Order_item_requests_SalvarTodosResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao salvar solicitações de item de pedido');
    } catch (err) {
      logError('useSOOrderItemRequests', 'saveAll', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const save = async (data: Order_item_requests_SalvarInput) => {
    log('useSOOrderItemRequests', 'save', 'Saving order item request', data);
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/salvar`, data, { inputContract: 'Order_item_requests_SalvarInput', responseContract: 'Order_item_requests_SalvarResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao salvar solicitação de item de pedido');
    } catch (err) {
      logError('useSOOrderItemRequests', 'save', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const remove = async (id: any) => {
    log('useSOOrderItemRequests', 'remove', 'Removing order item request', { id });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/remover/${id}`, null, { responseContract: 'Order_item_requests_RemoverResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao remover solicitação de item de pedido');
    } catch (err) {
      logError('useSOOrderItemRequests', 'remove', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const verify = async (id: number) => {
    log('useSOOrderItemRequests', 'verify', 'Verifying order item request', { id });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/verificar/${id}`, null, { inputContract: 'Order_item_requests_VerificarInput', responseContract: 'Order_item_requests_VerificarResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao verificar solicitação de item de pedido');
    } catch (err) {
      logError('useSOOrderItemRequests', 'verify', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const confirmExit = async (id: number, exitPhoto: string) => {
    log('useSOOrderItemRequests', 'confirmExit', 'Confirming order item request on exit', { id });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/confirmar_saida/${id}`, { exit_photo: exitPhoto }, { inputContract: 'Order_item_requests_ConfirmarSaidaInput', responseContract: 'Order_item_requests_VerificarResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao confirmar solicitação na saída');
    } catch (err) {
      logError('useSOOrderItemRequests', 'confirmExit', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const uploadPhoto = async (file: File) => {
    log('useSOOrderItemRequests', 'uploadPhoto', 'Uploading photo', { fileName: file.name });
    isLoading.value = true;
    error.value = null;
    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await httpService.post(`${basePath}/upload_photo`, formData, { inputContract: 'Order_item_requests_UploadPhotoInput', responseContract: 'Order_item_requests_UploadPhotoResponse' });
      if (response.success && response.dados) {
        return response.dados.url;
      }
      throw new Error(response.error || 'Erro ao enviar foto');
    } catch (err) {
      logError('useSOOrderItemRequests', 'uploadPhoto', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    fetchByOrderItem,
    saveAll,
    save,
    remove,
    verify,
    confirmExit,
    uploadPhoto
  };
}
